import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getServerList, getMasterData, getServerInfo, saveServerInfo,
  blockServer, unblockServer, removeServer,
  saveIntimation, getFirewall, saveFirewall
} from '../api';

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const ThContext = React.createContext({});

const Th = ({ label, sortKey, show = true, sticky = false }) => {
  const { sortConfig, colFilters, onSort, onFilterChange } = React.useContext(ThContext);
  if (!show) return null;
  const isStickyProps = sticky ? { position: 'sticky', right: 0, zIndex: 12, borderLeft: '1px solid #dee2e6' } : { zIndex: 10 };
  return (
    <th
      className="bg-white align-top p-1"
      style={{ whiteSpace: 'nowrap', ...isStickyProps }}
    >
      <div
        onClick={() => sortKey && onSort(sortKey)}
        className="d-flex align-items-center justify-content-between text-dark mb-1"
        style={{ cursor: sortKey ? 'pointer' : 'default', fontSize: '0.75rem', fontWeight: '700' }}
      >
        <span className="me-2">{label}</span>
        {sortKey && sortConfig.key === sortKey && (
          <i className={`bi bi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'} text-primary`} style={{ fontSize: '0.65rem' }}></i>
        )}
        {sortKey && sortConfig.key !== sortKey && (
          <i className="bi bi-arrow-down-up text-muted opacity-25" style={{ fontSize: '0.65rem' }}></i>
        )}
      </div>
      {sortKey && (
        <input
          type="text"
          className="form-control form-control-sm border-secondary shadow-none bg-light"
          style={{ fontSize: '0.75rem', padding: '0.1rem 0.3rem', height: '24px', minWidth: '70px' }}
          placeholder="Fil.."
          value={colFilters[sortKey] || ''}
          onChange={e => onFilterChange(sortKey, e.target.value)}
        />
      )}
    </th>
  );
};
const BLANK_SERVER_INFO = {
  SERVERIP: '', HOSTNAME: '', PASSWORD: '', WINCAL: '',
  ACRONIS: '', DCLOC: '', OS: '', DISK: '',
  CONSOLEKVM_IP: '', ZONE: '',
  SERVER_TYPE: 'VM',
  FW_ENABLED: false, FW_IP: '',
  VPN_ENABLED: false, VPN_IP: '', VPN_UNAME: '', VPN_PASS: '',
  REMARK: ''
};

const BLANK_INTIMATION = {
  TEMPLATE: '', ONDAY: '', IP_CHANGE_TYPE: '0'
};

const BLANK_FIREWALL = {
  MODEL_NO: '', SERVERIP: '', ADMIN_UNAME: '', PASSWORD: ''
};

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────────────────── */
const formatYMD = (val) => {
  if (!val) return '';
  const s = String(val).trim();
  // Match forms like 20251125 or 20251125 17:07
  const match = s.match(/^(\d{4})(\d{2})(\d{2})(?:\s+(.*))?$/);
  if (match) {
    const [, y, m, d, time] = match;
    const dateObj = new Date(y, parseInt(m, 10) - 1, d);
    if (!isNaN(dateObj)) {
      let dd = dateObj.getDate().toString().padStart(2, '0');
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      let mmm = monthNames[dateObj.getMonth()];
      let yyyy = dateObj.getFullYear();
      let fDate = `${dd} ${mmm} ${yyyy}`;
      if (time) fDate += ` ${time}`;
      return fDate;
    }
  }
  // Try standard Date fallback just in case
  const dStandard = new Date(s);
  if (!isNaN(dStandard) && dStandard.getFullYear() > 2000) {
    const pDate = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(dStandard);
    return s.includes(':') ? `${pDate} ${s.split(' ')[1] || ''}`.trim() : pDate;
  }
  return s;
};

export default function ServerManagement() {
  const navigate = useNavigate();

  /* ── State ── */
  const [saveSuccess, setSaveSuccess] = useState(null); // { ip, hostname }
  const [viewType, setViewType] = useState(0);
  const [searchIP, setSearchIP] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // master data
  const [masterData, setMasterData] = useState({
    acronis: [], dcLocations: [], osTypes: [], diskTypes: [], clusters: []
  });

  // Server Info modal
  const [showServerInfo, setShowServerInfo] = useState(false);
  const [serverInfoData, setServerInfoData] = useState(BLANK_SERVER_INFO);
  const [serverInfoOrig, setServerInfoOrig] = useState(null);   // original from API (for config table)
  const [serverInfoSaving, setServerInfoSaving] = useState(false);
  const [serverInfoError, setServerInfoError] = useState('');

  // Intimation modal
  const [showIntimation, setShowIntimation] = useState(false);
  const [intimationIP, setIntimationIP] = useState('');
  const [intimationData, setIntimationData] = useState(BLANK_INTIMATION);
  const [intimationSaving, setIntimationSaving] = useState(false);
  const [intimationError, setIntimationError] = useState('');

  // Firewall modal
  const [showFirewall, setShowFirewall] = useState(false);
  const [firewallData, setFirewallData] = useState(BLANK_FIREWALL);
  const [firewallSaving, setFirewallSaving] = useState(false);
  const [firewallError, setFirewallError] = useState('');

  // Track the row that corresponds to the open modal
  const [activeRow, setActiveRow] = useState(null);

  /* ── Load master data once ── */
  useEffect(() => {
    getMasterData()
      .then(r => {
        const d = r.data || {};
        // Backend returns {ID, NAME}; normalise to {VALUE, LABEL}
        const toOptions = (arr) =>
          (Array.isArray(arr) ? arr : []).map(i => ({
            VALUE: String(i.ID ?? i.id ?? ''),
            LABEL: String(i.NAME ?? i.name ?? ''),
          }));

        const dcLocOptions = toOptions(d.dcLoc);                      // API key is dcLoc
        const dcLocById = Object.fromEntries(dcLocOptions.map(o => [o.VALUE, o.LABEL]));

        // Clusters come as grouped dict { DC_ID: [{id, name}] } — flatten with DC name
        const clustersFlat = [];
        const rawClusters = d.clusters;
        if (rawClusters && typeof rawClusters === 'object' && !Array.isArray(rawClusters)) {
          Object.entries(rawClusters).forEach(([dcId, items]) => {
            const dcName = dcLocById[dcId] || `DC ${dcId}`;
            (Array.isArray(items) ? items : []).forEach(item => {
              clustersFlat.push({
                VALUE: String(item.id ?? ''),
                LABEL: String(item.name ?? ''),
                DCLOC: dcName,
              });
            });
          });
        }

        setMasterData({
          acronis:     toOptions(d.acronis),
          dcLocations: dcLocOptions,
          osTypes:     toOptions(d.consoleType),   // OS maps to ConTypeId (consoleType table)
          diskTypes:   toOptions(d.consoleType),   // Disk maps to same lookup for now
          clusters:    clustersFlat,
          templates:   (Array.isArray(d.templates) ? d.templates : []).map(i => ({
            VALUE: String(i.ID ?? ''),
            LABEL: String(i.SER_TEMPLATE ?? ''),
          })),
        });
      })
      .catch(() => { });
  }, []);

  /* ── Fetch list ── */
  const fetchList = useCallback((ip) => {
    setLoading(true);
    setError('');
    getServerList(viewType, ip || '')
      .then(r => setRows(r.data || []))
      .catch(e => setError(e?.response?.data?.message || 'Failed to load data.'))
      .finally(() => setLoading(false));
  }, [viewType]);

  useEffect(() => {
    fetchList('');
    setSearchIP('');
    setCurrentPage(1);
  }, [viewType]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Column visibility ── */
  const isView0 = viewType === 0;
  const isBlockView = viewType === 2 || viewType === 3;
  const showCoreRamEtc = !isBlockView;
  const showUname = viewType === 1 || viewType === 3;
  const showSeractReqtype = isBlockView;
  const showReqsts = isBlockView;
  const showIntimCol = !isView0;
  // Action column only visible for "New/Config Change" (0) and "Recent Last 2 Days" (1)
  const showActionCol = viewType === 0 || viewType === 1;
  const showServerInfoBtn = true;
  const showIntimBtn = true;
  const showFwBtn = true;
  const showActionBtn = isBlockView;

  /* ── Server Info modal ── */
  const openServerInfo = (row) => {
    setServerInfoError('');
    setActiveRow(row);
    getServerInfo({
      sono: row.SONO || '',
      sotype: row.SOTYPE || '',
      oppId: row.OPPID || 0,
      prvOppId: row.PRVOPPID || 0,
      subtype: row.SUBTYPE || ''
    })
      .then(r => {
        const info = r.data?.info || {};
        const config = r.data?.config || [];
        setServerInfoOrig({ CONFIG_ROWS: config });
        setServerInfoData({
          SERVERIP: info.SERVERIP ?? row.SERVERIP ?? '',
          HOSTNAME: info.HOSTNAME ?? row.HOSTNAME ?? '',
          PASSWORD: info.PASSWORD ?? '',
          WINCAL: info.WINCAL ?? info.WINDOWSCAL ?? '',
          ACRONIS: String(info.ACRONIS ?? row.ACRONIS ?? ''),
          DCLOC: String(info.DCLOC ?? row.DCLOC ?? ''),
          OS: String(info.OS ?? row.OS ?? ''),
          DISK: String(info.DISK ?? row.DISK ?? ''),
          CONSOLEKVM_IP: info.CONSOLEKVM_IP ?? '',
          ZONE: String(info.ZONE ?? ''),
          SERVER_TYPE: info.SERTYPE === 'DC' ? 'Dedicated' : 'VM',
          FW_ENABLED: !!info.Firewall_IP,
          FW_IP: info.Firewall_IP ?? '',
          VPN_ENABLED: !!info.ISVPN,
          VPN_IP: info.VPNSER ?? '',
          VPN_UNAME: info.VPNUNAME ?? '',
          VPN_PASS: info.VPNPWD ?? '',
          REMARK: info.REMARK ?? row.REMARK ?? ''
        });
        setShowServerInfo(true);
      })
      .catch(() => {
        setServerInfoOrig(null);
        setServerInfoData({ ...BLANK_SERVER_INFO, SERVERIP: row.SERVERIP || '', HOSTNAME: row.HOSTNAME || '' });
        setShowServerInfo(true);
      });
  };

  const saveServerInfoHandler = () => {
    setServerInfoSaving(true);
    setServerInfoError('');
    
    const acronisObj = masterData.acronis.find(a => String(a.VALUE) === String(serverInfoData.ACRONIS));
    const dclocObj = masterData.dcLocations.find(d => String(d.VALUE) === String(serverInfoData.DCLOC));

    const payload = {
      Dpid: activeRow?.DPID || 0,
      Sono: activeRow?.SONO || '',
      Sotype: activeRow?.SOTYPE || '',
      TallySrno: activeRow?.TALLYSRNO || '',
      Ip: serverInfoData.SERVERIP,
      Hostname: serverInfoData.HOSTNAME,
      WindowsCal: serverInfoData.WINCAL,
      Rack: activeRow?.RACK || '',
      AcronisId: acronisObj ? Number(acronisObj.VALUE) : 0,
      AcronisName: acronisObj ? acronisObj.LABEL : '',
      DcLocName: dclocObj ? dclocObj.VALUE : '',
      ConMstId: Number(serverInfoData.ZONE) || 0,
      ConTypeId: Number(serverInfoData.OS) || 0,
      IsVM: serverInfoData.SERVER_TYPE === 'VM',
      FirewallIp: serverInfoData.FW_ENABLED ? serverInfoData.FW_IP : '',
      IsVpn: serverInfoData.VPN_ENABLED,
      VpnIp: serverInfoData.VPN_IP,
      VpnUname: serverInfoData.VPN_UNAME,
      VpnPwd: serverInfoData.VPN_PASS,
      Remark: serverInfoData.REMARK
    };

    saveServerInfo(payload)
      .then(() => {
        setShowServerInfo(false);
        fetchList(searchIP);
        setSaveSuccess({ ip: payload.Ip, hostname: payload.Hostname });
        setTimeout(() => setSaveSuccess(null), 5000);
      })
      .catch(e => setServerInfoError(e?.response?.data?.message || 'Save failed.'))
      .finally(() => setServerInfoSaving(false));
  };

  /* ── Intimation modal ── */
  const openIntimation = (row) => {
    setIntimationError('');
    setActiveRow(row);
    setIntimationIP(row.SERVERIP || '');
    setIntimationData(BLANK_INTIMATION);
    setShowIntimation(true);
  };

  const saveIntimationHandler = () => {
    setIntimationSaving(true);
    setIntimationError('');
    
    // find index if using template select
    const payload = {
      Sono: activeRow?.SONO || '',
      Sotype: activeRow?.SOTYPE || '',
      TemplateId: Number(intimationData.TEMPLATE) || 1,
      TemplateText: 'Template Content Here', // Usually user inputs this or we map from ID
      OnDay: intimationData.ONDAY || '1',
      WithIp: intimationData.IP_CHANGE_TYPE === '1' || intimationData.IP_CHANGE_TYPE === '2',
      IntmId: 0
    };

    saveIntimation(payload)
      .then(() => setShowIntimation(false))
      .catch(e => setIntimationError(e?.response?.data?.message || 'Save failed.'))
      .finally(() => setIntimationSaving(false));
  };

  /* ── Firewall modal ── */
  const openFirewall = (row) => {
    setFirewallError('');
    setActiveRow(row);
    getFirewall({ sono: row.SONO || '', sotype: row.SOTYPE || '' })
      .then(r => {
        const d = r.data || {};
        setFirewallData({
          MODEL_NO: d.model || '',
          SERVERIP: d.serverIp || row.SERVERIP || '',
          ADMIN_UNAME: d.admin || '',
          PASSWORD: d.pwd || '' // Note: pwd wasn't returned from API intentionally, but leaving ''
        });
      })
      .catch(() => {
        setFirewallData({ ...BLANK_FIREWALL, SERVERIP: row.SERVERIP || '' });
      });
    setShowFirewall(true);
  };

  const saveFirewallHandler = () => {
    setFirewallSaving(true);
    setFirewallError('');
    const payload = {
      Sono: activeRow?.SONO || '',
      Sotype: activeRow?.SOTYPE || '',
      Model: firewallData.MODEL_NO,
      ServerIp: firewallData.SERVERIP,
      Admin: firewallData.ADMIN_UNAME,
      Pwd: firewallData.PASSWORD
    };
    saveFirewall(payload)
      .then(() => setShowFirewall(false))
      .catch(e => setFirewallError(e?.response?.data?.message || 'Save failed.'))
      .finally(() => setFirewallSaving(false));
  };

  /* ── Block / Unblock / Remove ── */
  const handleAction = (row) => {
    const reqtype = (row.REQTYPE || '').toLowerCase().trim();
    let apiFn;
    if (reqtype === 'to be block') apiFn = blockServer;
    else if (reqtype === 'to be unblock') apiFn = unblockServer;
    else if (reqtype === 'to be remove') apiFn = removeServer;
    else return;

    apiFn({ SERVERIP: row.SERVERIP, DPID: row.DPID, TALLYSRNO: row.TALLYSRNO })
      .then(() => fetchList(searchIP))
      .catch(e => setError(e?.response?.data?.message || 'Action failed.'));
  };

  /* ── Action button appearance ── */
  const getActionBtn = (row) => {
    if (row.REQSTS !== 'Pending') return null;
    const reqtype = (row.REQTYPE || '').toLowerCase().trim();
    if (reqtype === 'to be block') return { label: 'Block', cls: 'btn-danger' };
    if (reqtype === 'to be unblock') return { label: 'Unblock', cls: 'btn-success' };
    if (reqtype === 'to be remove') return { label: 'Remove', cls: 'btn-warning' };
    return null;
  };

  /* ── Cluster grouping for Zone dropdown (OS == 5) ── */
  const clustersByDC = (masterData.clusters || []).reduce((acc, c) => {
    const dc = c.DCLOC || 'Other';
    if (!acc[dc]) acc[dc] = [];
    acc[dc].push(c);
    return acc;
  }, {});

  /* ── Server Info modal ── */
  const configRows = serverInfoOrig?.CONFIG_ROWS || [];

  /* ── Sorting & Filtering ── */
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [colFilters, setColFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (key, val) => {
    setColFilters(prev => ({ ...prev, [key]: val }));
  };

  const processedRows = React.useMemo(() => {
    let result = [...rows];
    
    // 1. Column Filtering
    Object.keys(colFilters).forEach(key => {
      const filterVal = colFilters[key]?.toLowerCase();
      if (filterVal) {
        result = result.filter(r => String(r[key] || '').toLowerCase().includes(filterVal));
      }
    });

    // 2. Sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const valA = a[sortConfig.key] ?? '';
        const valB = b[sortConfig.key] ?? '';
        
        const numA = parseFloat(valA);
        const numB = parseFloat(valB);
        if (!isNaN(numA) && !isNaN(numB)) {
          return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
        }

        return sortConfig.direction === 'asc' 
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });
    }

    return result;
  }, [rows, sortConfig, colFilters]);

  // Reset to page 1 when filters or sort change
  useEffect(() => { setCurrentPage(1); }, [sortConfig, colFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalRows  = processedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const pagedRows  = processedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Page number list with ellipsis: [1] … [4][5][6] … [12]
  const pageNumbers = React.useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1].filter(p => p >= 1 && p <= totalPages));
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('…');
      result.push(sorted[i]);
    }
    return result;
  }, [totalPages, currentPage]);

  const pgBtnStyle = (active, disabled) => ({
    minWidth: 32, height: 32, padding: '0 8px',
    borderRadius: 7, border: active ? 'none' : '1px solid #dee2e6',
    background: active ? 'linear-gradient(135deg,#4f46e5,#6366f1)' : disabled ? '#f8f9fa' : '#fff',
    color: active ? '#fff' : disabled ? '#adb5bd' : '#495057',
    fontWeight: active ? 700 : 500, fontSize: '0.82rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    boxShadow: active ? '0 2px 8px rgba(99,102,241,0.35)' : 'none',
    transition: 'all 0.15s',
  });


  /* ────────────────────────────────────────────────────────────────────────
     RENDER
  ──────────────────────────────────────────────────────────────────────── */
  return (
    <div className="py-2">

      {/* ── Success Toast ── */}
      <style>{`
        @keyframes smSlideIn {
          from { opacity: 0; transform: translateX(110%); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes smSlideOut {
          from { opacity: 1; transform: translateX(0); }
          to   { opacity: 0; transform: translateX(110%); }
        }
        @keyframes smProgress {
          from { width: 100%; }
          to   { width: 0%; }
        }
        .sm-toast {
          animation: smSlideIn 0.45s cubic-bezier(.22,.68,0,1.2) both;
        }
        .sm-toast.hiding {
          animation: smSlideOut 0.35s ease forwards;
        }
      `}</style>

      {saveSuccess && (
        <div
          className="sm-toast"
          style={{
            position: 'fixed', top: 24, right: 24, zIndex: 9999,
            width: 340, borderRadius: 16,
            background: '#fff',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(25,135,84,0.12)',
            overflow: 'hidden',
            border: '1px solid #d1fae5',
          }}
        >
          {/* Green accent bar */}
          <div style={{ height: 5, background: 'linear-gradient(90deg,#22c55e,#16a34a)', position: 'relative' }}>
            <div style={{
              position: 'absolute', top: 0, left: 0, height: '100%',
              background: 'rgba(255,255,255,0.45)',
              animation: 'smProgress 5s linear forwards',
            }} />
          </div>

          <div style={{ padding: '18px 20px 20px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            {/* Icon */}
            <div style={{
              flexShrink: 0, width: 44, height: 44, borderRadius: '50%',
              background: 'linear-gradient(135deg,#22c55e,#16a34a)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(34,197,94,0.35)',
            }}>
              <i className="bi bi-check-lg text-white fs-5" />
            </div>

            {/* Text */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#166534', marginBottom: 4 }}>
                Server Configuration Saved!
              </div>
              <div style={{ fontSize: '0.82rem', color: '#4b5563', lineHeight: 1.5 }}>
                {saveSuccess.ip && (
                  <span>
                    <span style={{ fontWeight: 600, color: '#111827' }}>{saveSuccess.ip}</span>
                    {saveSuccess.hostname && (
                      <> &mdash; <span style={{ color: '#6b7280' }}>{saveSuccess.hostname}</span></>
                    )}
                    <br />
                  </span>
                )}
                Configuration has been committed successfully.
              </div>
            </div>

            {/* Close */}
            <button
              onClick={() => setSaveSuccess(null)}
              style={{
                flexShrink: 0, background: 'none', border: 'none',
                cursor: 'pointer', color: '#9ca3af', padding: '2px 4px',
                fontSize: '1rem', lineHeight: 1,
                borderRadius: 6, transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#374151'}
              onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
              title="Dismiss"
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>
        </div>
      )}

      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="fw-bold text-dark mb-0">Dashboard</h4>
        <div className="d-flex align-items-center gap-3">
          <button
            className="btn btn-outline-primary shadow-sm"
            onClick={() => navigate('/cloud-server-ip')}
          >
            <i className="bi bi-cloud me-2" />Cloud Server IPs
          </button>
          <button
            className="btn btn-primary shadow-sm"
            onClick={() => navigate('/update-server-details')}
          >
            <i className="bi bi-pencil-square me-2" />Update Server Details
          </button>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="filter-bar d-flex flex-wrap align-items-center gap-3 mb-4">
        {/* View Type */}
        <div className="d-flex align-items-center">
          <label className="text-secondary fw-medium small me-2 mb-0">View Type:</label>
          <select
            className="form-select"
            style={{ width: 230 }}
            value={viewType}
            onChange={e => setViewType(Number(e.target.value))}
          >
            <option value={0}>New / Config Change</option>
            <option value={1}>Recent Last 2 Days</option>
            <option value={2}>Blocked Pending</option>
            <option value={3}>Recent Block / Remove</option>
          </select>
        </div>

        {/* Search IP  */}
        <div className="d-flex align-items-center">
          <div className="input-group">
            <span className="input-group-text bg-light border-end-0 text-secondary">
              <i className="bi bi-search" />
            </span>
            <input
              type="text"
              className="form-control border-start-0 ps-0"
              style={{ width: 160 }}
              placeholder="Search IP..."
              value={searchIP}
              onChange={e => setSearchIP(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchList(searchIP)}
            />
          </div>
        </div>

        <button className="btn btn-outline-primary fw-semibold bg-light" onClick={() => fetchList(searchIP)}>
          Go
        </button>
        <button className="btn btn-light border text-secondary fw-semibold" onClick={() => { setSearchIP(''); fetchList(''); }}>
          <i className="bi bi-arrow-clockwise me-1" />Reset
        </button>
        {Object.values(colFilters).some(v => v) && (
          <button className="btn btn-outline-danger fw-semibold" onClick={() => setColFilters({})}>
            <i className="bi bi-x-circle me-1" />Clear Filters
          </button>
        )}

        {/* Total badge */}
        <div className="ms-auto">
          <span className="badge bg-light text-secondary border px-3 py-2 fs-6 fw-medium shadow-sm">
            Total: <span className="text-dark fw-bold">{rows.length}</span>
            {totalRows !== rows.length && <> &nbsp;(filtered: <span className="text-primary fw-bold">{totalRows}</span>)</>}
          </span>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="alert alert-danger py-2 fw-medium rounded-3">{error}</div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div className="text-center py-5 my-5">
          <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} />
          <div className="mt-3 text-secondary fw-medium">Loading records...</div>
        </div>
      ) : (
        <>
        <div className="table-responsive shadow-sm border rounded" style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
          <table className="table table-hover table-sm align-middle mb-0" style={{ fontSize: '0.8rem' }}>
            <ThContext.Provider value={{ sortConfig, colFilters, onSort: handleSort, onFilterChange: handleFilterChange }}>
            <thead className="sticky-top shadow-sm" style={{ zIndex: 11 }}>
              <tr>
                {(isView0 || viewType === 1) ? (
                  <>
                    <Th label="Date" sortKey="DATE" />
                    <Th label="Approval Date" sortKey="APRVDT" />
                    <Th label="Subscription ID" sortKey="SONO" />
                    <Th label="Type" sortKey="SUBTYPE" />
                    <Th label="Month" sortKey="NOOFMONTH" />
                    <Th label="CORE" sortKey="CORE" />
                    <Th label="RAM" sortKey="RAM" />
                    <Th label="SSD" sortKey="SSD" />
                    <Th label="Server IP" sortKey="SERVERIP" />
                    <Th label="Host Name" sortKey="HOSTNAME" />
                    <Th label="Acronis" sortKey="ACRONIS" />
                    <Th label="DC Location" sortKey="DCLOC" />
                    <Th label="Console IP" sortKey="CONSOLEKVM_IP" />
                    {viewType === 1 && <Th label="Updated By" sortKey="UNAME" />}
                  </>
                ) : viewType === 2 ? (
                  <>
                    <Th label="Date" sortKey="DATE" />
                    <Th label="Approval Date" sortKey="APRVDT" />
                    <Th label="Subscription ID" sortKey="SONO" />
                    <Th label="Type" sortKey="SUBTYPE" />
                    <Th label="Server IP" sortKey="SERVERIP" />
                    <Th label="Host Name" sortKey="HOSTNAME" />
                    <Th label="Acronis" sortKey="ACRONIS" />
                    <Th label="DC Location" sortKey="DCLOC" />
                    <Th label="Console IP" sortKey="CONSOLEKVM_IP" />
                    <Th label="Action Date" sortKey="SERACTDT_DISP" />
                    <Th label="Req Type" sortKey="REQTYPE" />
                    <Th label="Req Status" sortKey="REQSTS" />
                  </>
                ) : (
                  <>
                    <Th label="Date" sortKey="DATE" />
                    <Th label="Approval Date" sortKey="APRVDT" />
                    <Th label="Subscription ID" sortKey="SONO" />
                    <Th label="Type" sortKey="SUBTYPE" />
                    <Th label="Month" sortKey="NOOFMONTH" />
                    <Th label="CORE" sortKey="CORE" />
                    <Th label="RAM" sortKey="RAM" />
                    <Th label="SSD" sortKey="SSD" />
                    <Th label="Server IP" sortKey="SERVERIP" />
                    <Th label="Host Name" sortKey="HOSTNAME" />
                    <Th label="Acronis" sortKey="ACRONIS" />
                    <Th label="DC Location" sortKey="DCLOC" />
                    <Th label="Console IP" sortKey="CONSOLEKVM_IP" />
                    <Th label="Action Date" sortKey="SERACTDT_DISP" />
                    <Th label="Req Type" sortKey="REQTYPE" />
                    <Th label="Req Status" sortKey="REQSTS" />
                    <Th label="Updated By" sortKey="UNAME" />
                  </>
                )}
                <Th label="ACTIONS" show={showActionCol} sticky={true} />
              </tr>
            </thead>
            </ThContext.Provider>
            <tbody>
              {processedRows.length === 0 ? (
                <tr>
                  <td colSpan={30} className="text-center text-muted py-4">No records found.</td>
                </tr>
              ) : pagedRows.map((row, idx) => {
                const actionBtn = showActionBtn ? getActionBtn(row) : null;
                return (
                  <tr key={idx} style={{ whiteSpace: 'nowrap' }}>
                    {(isView0 || viewType === 1) ? (
                      <>
                        <td className="p-1">{formatYMD(row.DATE || row.SERDATE_DISP || row.SERDATE)}</td>
                        <td className="p-1">{formatYMD(row.APRVDT)}</td>
                        <td className="p-1">{row.SONO}</td>
                        <td className="p-1">{row.SUBTYPE}</td>
                        <td className="p-1">{row.NOOFMONTH}</td>
                        <td className="p-1">{row.CORE}</td>
                        <td className="p-1">{row.RAM}</td>
                        <td className="p-1">{row.SSD}</td>
                        <td className="p-1 fw-medium text-dark">{row.SERVERIP}</td>
                        <td className="p-1">{row.HOSTNAME}</td>
                        <td className="p-1">{row.ACRONIS}</td>
                        <td className="p-1">{row.DCLOC}</td>
                        <td className="p-1">{row.CONSOLEKVM_IP}</td>
                        {viewType === 1 && <td className="p-1">{row.UNAME}</td>}
                      </>
                    ) : viewType === 2 ? (
                      <>
                        <td className="p-1">{formatYMD(row.DATE || row.SERDATE_DISP || row.SERDATE)}</td>
                        <td className="p-1">{formatYMD(row.APRVDT)}</td>
                        <td className="p-1">{row.SONO}</td>
                        <td className="p-1">{row.SUBTYPE}</td>
                        <td className="p-1 fw-medium text-dark">{row.SERVERIP}</td>
                        <td className="p-1">{row.HOSTNAME}</td>
                        <td className="p-1">{row.ACRONIS}</td>
                        <td className="p-1">{row.DCLOC}</td>
                        <td className="p-1">{row.CONSOLEKVM_IP}</td>
                        <td className="p-1">{row.SERACTDT_DISP}</td>
                        <td className="p-1">{row.REQTYPE}</td>
                        <td className="p-1">
                          {row.REQSTS === 'Done'
                            ? <span className="badge bg-success text-white shadow-sm fw-medium px-2 py-1">Done</span>
                            : row.REQSTS === 'Pending'
                              ? <span className="badge bg-warning text-dark shadow-sm fw-medium px-2 py-1">Pending</span>
                              : <span className="badge bg-light text-secondary border fw-medium px-2 py-1">{row.REQSTS}</span>}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="p-1">{formatYMD(row.DATE || row.SERDATE_DISP || row.SERDATE)}</td>
                        <td className="p-1">{formatYMD(row.APRVDT)}</td>
                        <td className="p-1">{row.SONO}</td>
                        <td className="p-1">{row.SUBTYPE}</td>
                        <td className="p-1">{row.NOOFMONTH}</td>
                        <td className="p-1">{row.CORE}</td>
                        <td className="p-1">{row.RAM}</td>
                        <td className="p-1">{row.SSD}</td>
                        <td className="p-1 fw-medium text-dark">{row.SERVERIP}</td>
                        <td className="p-1">{row.HOSTNAME}</td>
                        <td className="p-1">{row.ACRONIS}</td>
                        <td className="p-1">{row.DCLOC}</td>
                        <td className="p-1">{row.CONSOLEKVM_IP}</td>
                        <td className="p-1">{row.SERACTDT_DISP}</td>
                        <td className="p-1">{row.REQTYPE}</td>
                        <td className="p-1">
                          {row.REQSTS === 'Done'
                            ? <span className="badge bg-success text-white shadow-sm fw-medium px-2 py-1">Done</span>
                            : row.REQSTS === 'Pending'
                              ? <span className="badge bg-warning text-dark shadow-sm fw-medium px-2 py-1">Pending</span>
                              : <span className="badge bg-light text-secondary border fw-medium px-2 py-1">{row.REQSTS}</span>}
                        </td>
                        <td className="p-1">{row.UNAME}</td>
                      </>
                    )}
                    {showActionCol && (
                      <td className="text-nowrap p-1 align-middle" style={{ position: 'sticky', right: 0, zIndex: 5, background: '#fff', borderLeft: '1px solid #dee2e6' }}>
                        <div className="d-flex gap-2">
                          {showServerInfoBtn && (
                            <button
                              className="btn btn-sm btn-light border text-primary"
                              title="Server Info"
                              onClick={() => openServerInfo(row)}
                            >
                              <i className="bi bi-server" />
                            </button>
                          )}
                          {showIntimBtn && (
                            <button
                              className="btn btn-sm btn-light border text-warning"
                              title="Intimation"
                              onClick={() => openIntimation(row)}
                            >
                              <i className="bi bi-bell-fill" />
                            </button>
                          )}
                          {showFwBtn && (
                            <button
                              className="btn btn-sm btn-light border text-success"
                              title="Firewall"
                              onClick={() => openFirewall(row)}
                            >
                              <i className="bi bi-shield-check" />
                            </button>
                          )}
                          {showActionBtn && actionBtn && (
                            <button
                              className={`btn btn-sm shadow-sm ${actionBtn.cls}`}
                              onClick={() => handleAction(row)}
                            >
                              {actionBtn.label}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Pagination bar ── */}
        {totalRows > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 12,
            padding: '12px 16px',
            background: '#f8f9ff',
            borderTop: '1px solid #e9ecef',
            borderRadius: '0 0 8px 8px',
          }}>

            {/* Left: record count + page size */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.82rem', color: '#6c757d' }}>
              <span>
                Showing&nbsp;
                <strong style={{ color: '#212529' }}>
                  {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalRows)}
                </strong>
                &nbsp;of&nbsp;
                <strong style={{ color: '#212529' }}>{totalRows}</strong>
                &nbsp;records
              </span>
              <span style={{ color: '#dee2e6' }}>|</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0 }}>
                Rows&nbsp;per&nbsp;page
                <select
                  value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                  style={{
                    border: '1px solid #dee2e6', borderRadius: 6,
                    padding: '2px 6px', fontSize: '0.8rem',
                    background: '#fff', color: '#212529', cursor: 'pointer',
                  }}
                >
                  {[10, 15, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>

            {/* Center: page buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {/* Prev */}
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={pgBtnStyle(false, currentPage === 1)}
                title="Previous page"
              >
                <i className="bi bi-chevron-left" style={{ fontSize: '0.75rem' }} />
              </button>

              {pageNumbers.map((p, i) =>
                p === '…' ? (
                  <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: '#adb5bd', fontSize: '0.85rem', userSelect: 'none' }}>…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    style={pgBtnStyle(p === currentPage, false)}
                  >
                    {p}
                  </button>
                )
              )}

              {/* Next */}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={pgBtnStyle(false, currentPage === totalPages)}
                title="Next page"
              >
                <i className="bi bi-chevron-right" style={{ fontSize: '0.75rem' }} />
              </button>
            </div>

            {/* Right: total pages */}
            <div style={{ fontSize: '0.82rem', color: '#6c757d' }}>
              Page&nbsp;<strong style={{ color: '#212529' }}>{currentPage}</strong>&nbsp;of&nbsp;
              <strong style={{ color: '#212529' }}>{totalPages}</strong>
            </div>
          </div>
        )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MODAL 1 — SERVER INFO
      ══════════════════════════════════════════════════════════════════ */}
      {showServerInfo && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(3px)' }}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" style={{ pointerEvents: 'auto' }}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
              <div className="modal-header bg-gradient bg-primary text-white border-bottom-0 pb-3" style={{ borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                <h5 className="modal-title fw-bold d-flex align-items-center">
                  <span className="bg-white bg-opacity-25 rounded p-2 me-3 shadow-sm d-inline-flex"><i className="bi bi-hdd-network-fill fs-5" /></span>
                  Server Information Pipeline
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white shadow-none"
                  onClick={() => setShowServerInfo(false)}
                />
              </div>
              <div className="modal-body bg-light p-4">
                {serverInfoError && (
                  <div className="alert alert-danger d-flex align-items-center py-2 border-0 shadow-sm" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2 fs-5"></i>
                    <div>{serverInfoError}</div>
                  </div>
                )}
                <div className="row g-4">
                  {/* Left Column */}
                  <div className="col-lg-8">
                    {/* Basic Info Card */}
                    <div className="card border-0 shadow-sm rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom-0 pt-3 pb-0">
                        <h6 className="fw-bold text-primary mb-0"><i className="bi bi-info-circle me-2"></i>Basic Identity</h6>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary small mb-1">Server IP <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control bg-light border-0 shadow-none px-3 py-2"
                              value={serverInfoData.SERVERIP}
                              onChange={e => setServerInfoData(p => ({ ...p, SERVERIP: e.target.value }))}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary small mb-1">Hostname <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control bg-light border-0 shadow-none px-3 py-2"
                              value={serverInfoData.HOSTNAME}
                              onChange={e => setServerInfoData(p => ({ ...p, HOSTNAME: e.target.value }))}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary small mb-1">Password <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control bg-light border-0 shadow-none px-3 py-2"
                              value={serverInfoData.PASSWORD}
                              onChange={e => setServerInfoData(p => ({ ...p, PASSWORD: e.target.value }))}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary small mb-1">Windows CAL</label>
                            <input
                              type="text"
                              className="form-control bg-light border-0 shadow-none px-3 py-2"
                              value={serverInfoData.WINCAL}
                              onChange={e => setServerInfoData(p => ({ ...p, WINCAL: e.target.value }))}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Specifications Card */}
                    <div className="card border-0 shadow-sm rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom-0 pt-3 pb-0">
                        <h6 className="fw-bold text-primary mb-0"><i className="bi bi-cpu me-2"></i>Specifications & Environment</h6>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-4">
                            <label className="form-label fw-semibold text-secondary small mb-1">OS <span className="text-danger">*</span></label>
                            <select
                              className="form-select bg-light border-0 shadow-none px-3 py-2"
                              value={serverInfoData.OS}
                              onChange={e => setServerInfoData(p => ({ ...p, OS: e.target.value }))}
                            >
                              <option value="">-- Select --</option>
                              {(masterData.osTypes || []).map(o => (
                                <option key={o.VALUE} value={o.VALUE}>{o.LABEL}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-4">
                            <label className="form-label fw-semibold text-secondary small mb-1">DC Location <span className="text-danger">*</span></label>
                            <select
                              className="form-select bg-light border-0 shadow-none px-3 py-2"
                              value={serverInfoData.DCLOC}
                              onChange={e => setServerInfoData(p => ({ ...p, DCLOC: e.target.value }))}
                            >
                              <option value="">-- Select --</option>
                              {(masterData.dcLocations || []).map(d => (
                                <option key={d.VALUE} value={d.VALUE}>{d.LABEL}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-4">
                            <label className="form-label fw-semibold text-secondary small mb-1">Acronis <span className="text-danger">*</span></label>
                            <select
                              className="form-select bg-light border-0 shadow-none px-3 py-2"
                              value={serverInfoData.ACRONIS}
                              onChange={e => setServerInfoData(p => ({ ...p, ACRONIS: e.target.value }))}
                            >
                              <option value="">-- Select --</option>
                              {(masterData.acronis || []).map(a => (
                                <option key={a.VALUE} value={a.VALUE}>{a.LABEL}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-4">
                            <label className="form-label fw-semibold text-secondary small mb-1">Disk Layout <span className="text-danger">*</span></label>
                            <select
                              className="form-select bg-light border-0 shadow-none px-3 py-2"
                              value={serverInfoData.DISK}
                              onChange={e => setServerInfoData(p => ({ ...p, DISK: e.target.value }))}
                            >
                              <option value="">-- Select --</option>
                              {(masterData.diskTypes || []).map(d => (
                                <option key={d.VALUE} value={d.VALUE}>{d.LABEL}</option>
                              ))}
                            </select>
                          </div>
                          {serverInfoData.OS !== '5' ? (
                            <div className="col-md-4">
                              <label className="form-label fw-semibold text-secondary small mb-1">Console / KVM IP</label>
                              <input
                                type="text"
                                className="form-control bg-light border-0 shadow-none px-3 py-2"
                                value={serverInfoData.CONSOLEKVM_IP}
                                onChange={e => setServerInfoData(p => ({ ...p, CONSOLEKVM_IP: e.target.value }))}
                              />
                            </div>
                          ) : (
                            <div className="col-md-4">
                              <label className="form-label fw-semibold text-secondary small mb-1">Compute Zone</label>
                              <select
                                className="form-select bg-light border-0 shadow-none px-3 py-2"
                                value={serverInfoData.ZONE}
                                onChange={e => setServerInfoData(p => ({ ...p, ZONE: e.target.value }))}
                              >
                                <option value="">-- Select --</option>
                                {Object.entries(clustersByDC).map(([dc, clusters]) => (
                                  <optgroup key={dc} label={dc}>
                                    {clusters.map(c => (
                                      <option key={c.VALUE} value={c.VALUE}>{c.LABEL}</option>
                                    ))}
                                  </optgroup>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Server Configuration Table */}
                    {configRows.length > 0 && (
                      <div className="card border-0 shadow-sm rounded-3">
                        <div className="card-header bg-white border-bottom-0 pt-3 pb-2">
                          <h6 className="fw-bold text-dark mb-0"><i className="bi bi-clock-history me-2 text-primary"></i>Configuration History Comparison</h6>
                        </div>
                        <div className="card-body p-0 border-top">
                          <div className="table-responsive">
                            <table className="table table-hover table-sm mb-0" style={{ fontSize: 13 }}>
                              <thead className="bg-light text-secondary">
                                <tr>
                                  <th className="px-4 py-2 border-bottom-0">Parameter</th>
                                  <th className="py-2 border-bottom-0">Previous Value</th>
                                  <th className="py-2 border-bottom-0 text-primary">New Setup</th>
                                </tr>
                              </thead>
                              <tbody>
                                {configRows.map((cr, i) => {
                                  const highlight = cr.PRVCONF !== cr.NEWCONF;
                                  return (
                                    <tr key={i} className={highlight ? "table-warning" : ""}>
                                      <td className="px-4 fw-medium text-dark">{cr.PARTICULAR}</td>
                                      <td>{cr.PRVCONF}</td>
                                      <td className={highlight ? "fw-bold text-success" : ""}>{cr.NEWCONF}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column */}
                  <div className="col-lg-4">

                    {/* Top Right Card: Hardware Mode */}
                    <div className="card border-0 shadow-sm rounded-3 mb-4">
                      <div className="card-body">
                        <label className="form-label fw-bold text-dark mb-3"><i className="bi bi-gear-fill text-primary me-2"></i>Deployment Mode</label>
                        <div className="btn-group w-100" role="group">
                          {['VM', 'Dedicated'].map(t => (
                            <React.Fragment key={t}>
                              <input
                                type="radio"
                                className="btn-check"
                                name="SERVER_TYPE"
                                id={`stype-${t}`}
                                value={t}
                                checked={serverInfoData.SERVER_TYPE === t}
                                onChange={() => setServerInfoData(p => ({ ...p, SERVER_TYPE: t }))}
                              />
                              <label className={`btn ${serverInfoData.SERVER_TYPE === t ? 'btn-primary shadow-sm' : 'btn-outline-primary bg-white'}`} htmlFor={`stype-${t}`}>
                                {t === 'VM' ? <i className="bi bi-cloud-fill me-2" /> : <i className="bi bi-hdd-fill me-2" />}
                                {t}
                              </label>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Network & Security Card */}
                    <div className="card border-0 shadow-sm rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom-0 pt-3 pb-0">
                        <h6 className="fw-bold text-primary mb-0"><i className="bi bi-shield-lock-fill me-2"></i>Network Security</h6>
                      </div>
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between mb-3 bg-light p-3 rounded">
                          <div>
                            <span className="fw-semibold text-dark d-block">IP Firewall Layer</span>
                            <span className="text-secondary small">Filter incoming traffic</span>
                          </div>
                          <div className="form-check form-switch fs-4 mb-0">
                            <input
                              className="form-check-input mt-0"
                              type="checkbox"
                              role="switch"
                              checked={serverInfoData.FW_ENABLED}
                              onChange={e => setServerInfoData(p => ({ ...p, FW_ENABLED: e.target.checked }))}
                            />
                          </div>
                        </div>
                        {serverInfoData.FW_ENABLED && (
                          <div className="mb-4 animate__animated animate__fadeIn">
                            <input
                              type="text"
                              className="form-control border-primary border-opacity-50 px-3 py-2 shadow-sm"
                              placeholder="Assigned Firewall IP Segment"
                              value={serverInfoData.FW_IP}
                              onChange={e => setServerInfoData(p => ({ ...p, FW_IP: e.target.value }))}
                            />
                          </div>
                        )}

                        <div className="d-flex align-items-center justify-content-between mb-3 bg-light p-3 rounded mt-2">
                          <div>
                            <span className="fw-semibold text-dark d-block">VPN Tunneling</span>
                            <span className="text-secondary small">Secure remote access</span>
                          </div>
                          <div className="form-check form-switch fs-4 mb-0">
                            <input
                              className="form-check-input mt-0"
                              type="checkbox"
                              role="switch"
                              checked={serverInfoData.VPN_ENABLED}
                              onChange={e => setServerInfoData(p => ({ ...p, VPN_ENABLED: e.target.checked }))}
                            />
                          </div>
                        </div>
                        {serverInfoData.VPN_ENABLED && (
                          <div className="row g-2 animate__animated animate__fadeIn">
                            <div className="col-12">
                              <input
                                type="text"
                                className="form-control border-primary border-opacity-50"
                                placeholder="VPN Endpoint IP"
                                value={serverInfoData.VPN_IP}
                                onChange={e => setServerInfoData(p => ({ ...p, VPN_IP: e.target.value }))}
                              />
                            </div>
                            <div className="col-6">
                              <input
                                type="text"
                                className="form-control border-primary border-opacity-50"
                                placeholder="Username"
                                value={serverInfoData.VPN_UNAME}
                                onChange={e => setServerInfoData(p => ({ ...p, VPN_UNAME: e.target.value }))}
                              />
                            </div>
                            <div className="col-6">
                              <input
                                type="text"
                                className="form-control border-primary border-opacity-50"
                                placeholder="Password"
                                value={serverInfoData.VPN_PASS}
                                onChange={e => setServerInfoData(p => ({ ...p, VPN_PASS: e.target.value }))}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Remarks Card */}
                    <div className="card shadow-sm border-0 rounded-3">
                      <div className="card-body">
                        <label className="form-label fw-semibold text-dark mb-2"><i className="bi bi-journal-text me-2 text-primary"></i>Operational Remarks</label>
                        <textarea
                          className="form-control bg-light border-0 shadow-none px-3 py-2"
                          rows={3}
                          placeholder="Add any additional context or metadata..."
                          value={serverInfoData.REMARK}
                          onChange={e => setServerInfoData(p => ({ ...p, REMARK: e.target.value }))}
                        />
                      </div>
                    </div>

                  </div>
                </div>
              </div>
              <div className="modal-footer bg-white border-top-0 pt-0 pb-4 px-4">
                <button
                  className="btn btn-light btn-lg px-4 border shadow-sm fw-medium text-secondary"
                  onClick={() => setShowServerInfo(false)}
                  disabled={serverInfoSaving}
                >
                  Discard Changes
                </button>
                {isView0 && (
                  <button
                    className="btn btn-primary btn-lg px-5 shadow fw-semibold"
                    style={{ borderRadius: '8px' }}
                    onClick={saveServerInfoHandler}
                    disabled={serverInfoSaving}
                  >
                    {serverInfoSaving
                      ? <><span className="spinner-border spinner-border-sm me-2" />Committing...</>
                      : <><i className="bi bi-cloud-check-fill me-2" />Save Configuration</>}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MODAL 2 — INTIMATION
      ══════════════════════════════════════════════════════════════════ */}
      {showIntimation && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog" style={{ pointerEvents: 'auto' }}>
            <div className="modal-content">
              <div className="modal-header bg-warning">
                <h5 className="modal-title">
                  <i className="bi bi-bell me-2" />Intimation — {intimationIP}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowIntimation(false)}
                />
              </div>
              <div className="modal-body">
                {intimationError && (
                  <div className="alert alert-danger py-2">{intimationError}</div>
                )}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Template</label>
                  <select
                    className="form-select form-select-sm"
                    value={intimationData.TEMPLATE}
                    onChange={e => setIntimationData(p => ({ ...p, TEMPLATE: e.target.value }))}
                  >
                    <option value="">-- Select Template --</option>
                    <option value="1">Template 1</option>
                    <option value="2">Template 2</option>
                    <option value="3">Template 3</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">On Day</label>
                  <select
                    className="form-select form-select-sm"
                    value={intimationData.ONDAY}
                    onChange={e => setIntimationData(p => ({ ...p, ONDAY: e.target.value }))}
                  >
                    <option value="">-- Select Day --</option>
                    {[...Array(31)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">IP Change Type</label>
                  <div className="d-flex gap-3 mt-1">
                    {[{ val: '0', lbl: 'No Change' }, { val: '1', lbl: 'IP Changed' }, { val: '2', lbl: 'New IP' }].map(opt => (
                      <div className="form-check" key={opt.val}>
                        <input
                          className="form-check-input"
                          type="radio"
                          id={`ipct-${opt.val}`}
                          name="IP_CHANGE_TYPE"
                          value={opt.val}
                          checked={intimationData.IP_CHANGE_TYPE === opt.val}
                          onChange={() => setIntimationData(p => ({ ...p, IP_CHANGE_TYPE: opt.val }))}
                        />
                        <label className="form-check-label" htmlFor={`ipct-${opt.val}`}>{opt.lbl}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowIntimation(false)}
                  disabled={intimationSaving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-warning btn-sm"
                  onClick={saveIntimationHandler}
                  disabled={intimationSaving}
                >
                  {intimationSaving
                    ? <><span className="spinner-border spinner-border-sm me-1" />Saving...</>
                    : <><i className="bi bi-save me-1" />Save</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MODAL 3 — FIREWALL
      ══════════════════════════════════════════════════════════════════ */}
      {showFirewall && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog" style={{ pointerEvents: 'auto' }}>
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">
                  <i className="bi bi-shield-check me-2" />Firewall
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowFirewall(false)}
                />
              </div>
              <div className="modal-body">
                {firewallError && (
                  <div className="alert alert-danger py-2">{firewallError}</div>
                )}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Model No</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={firewallData.MODEL_NO}
                    onChange={e => setFirewallData(p => ({ ...p, MODEL_NO: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Server IP</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={firewallData.SERVERIP}
                    onChange={e => setFirewallData(p => ({ ...p, SERVERIP: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Admin Username</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={firewallData.ADMIN_UNAME}
                    onChange={e => setFirewallData(p => ({ ...p, ADMIN_UNAME: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Password</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={firewallData.PASSWORD}
                    onChange={e => setFirewallData(p => ({ ...p, PASSWORD: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowFirewall(false)}
                  disabled={firewallSaving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success btn-sm"
                  onClick={saveFirewallHandler}
                  disabled={firewallSaving}
                >
                  {firewallSaving
                    ? <><span className="spinner-border spinner-border-sm me-1" />Saving...</>
                    : <><i className="bi bi-save me-1" />Save</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
